import { ViewSidebar } from "@mui/icons-material";
import { Typography, Tooltip } from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import fetchAndCacheImage from "./fetchandCatchImages";
import axios from "../API/API";
import { FILE_SERVICE_BASE_URL, BASE_URL } from "../constant/apiUrl";
import { showErrorToast, showSuccessToast } from "./Toast/toast";
import {
  downloadFileToLocal,
  getLanguageLookUpData,
  deleteCuratorFile,
  validateVttFile,
} from "../API/curatorApi";
import noPreviewImage from "../assets/no-preview.png";
const token = localStorage.getItem("auth_token");

/**
 * Validates thumbnail aspect ratio (16:9)
 * @param {File} file - The image file to validate
 * @returns {Promise<Object>} Promise that resolves with validation result or rejects with error
 */
export const validateThumbnailAspectRatio = (file, showToast = true) => {
  return new Promise((resolve, reject) => {
    // Check if file is provided
    if (!file) {
      reject(new Error("Thumbnail - No file provided"));
      return;
    }

    // Check if file is an image
    if (!file.type || !file.type.startsWith("image/")) {
      const error = "Thumbnail - Please select a valid image file";
      if (showToast) {
        showErrorToast(error);
      }
      reject(new Error(error));
      return;
    }

    // Create an image object to load and check dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      // Clean up the object URL
      URL.revokeObjectURL(objectUrl);

      const width = img.width;
      const height = img.height;
      const aspectRatio = width / height;
      const targetAspectRatio = 16 / 9; // 1.777...
      const tolerance = 0.01; // Allow small tolerance for floating point precision

      // Check if aspect ratio is approximately 16:9
      if (Math.abs(aspectRatio - targetAspectRatio) <= tolerance) {
        resolve({ isValid: true, width, height, aspectRatio });
      } else {
        const error = `Thumbnail - Image aspect ratio must be 16:9. Current ratio is ${width}:${height} (${aspectRatio.toFixed(
          2
        )}:1)`;
        if (showToast) {
          showErrorToast(error);
        }
        reject(new Error(error));
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      const error =
        "Thumbnail - Failed to load image. Please select a valid image file.";
      if (showToast) {
        showErrorToast(error);
      }
      reject(new Error(error));
    };

    img.src = objectUrl;
  });
};

/**
 * Validates thumbnail aspect ratio (16:9) from URL or fileId
 * @param {string} url - The image URL to validate
 * @param {string} fileId - Optional fileId to fetch image if URL is not available
 * @returns {Promise<Object>} Promise that resolves with validation result or rejects with error
 */
export const validateThumbnailAspectRatioFromUrl = (url, fileId = null) => {
  console.log(url, fileId, "url, fileId");
  return new Promise((resolve, reject) => {
    let imageUrl = url;

    // If no URL but fileId is provided fetch the image
    // if (!imageUrl && fileId) {
    //   try {
    //     imageUrl = await fetchAndCacheImage(fileId, null, null, true);
    //     if (!imageUrl) {
    //       reject(new Error("Failed to fetch image from fileId"));
    //       return;
    //     }
    //   } catch (error) {
    //     reject(new Error("Failed to fetch image from fileId"));
    //     return;
    //   }
    // }

    // Check if URL is provided
    if (!imageUrl) {
      reject(new Error("Thumbnail - No image URL or fileId provided"));
      return;
    }

    // Create an image object to load and check dimensions
    const img = new Image();

    // Set crossOrigin only for external URLs (not blob URLs)
    // if (imageUrl) {
    //   img.crossOrigin = "anonymous";
    // }

    // Add timeout to handle cases where image doesn't load
    const timeout = setTimeout(() => {
      reject(
        new Error(
          "Thumbnail - Image loading timeout. Please check if the image URL is valid."
        )
      );
    }, 10000); // 10 second timeout
    console.log(imageUrl, "imageUrl");
    console.log(img, "img");
    img.onload = () => {
      clearTimeout(timeout);
      const width = img.width;
      const height = img.height;
      console.log(width, height, "width, height");

      // Check if image has valid dimensions
      if (width === 0 || height === 0) {
        reject(
          new Error(
            "Thumbnail - Invalid image dimensions. Image may not be loaded correctly."
          )
        );
        return;
      }

      const aspectRatio = width / height;
      const targetAspectRatio = 16 / 9; // 1.777...
      const tolerance = 0.01; // Allow small tolerance for floating point precision

      // Check if aspect ratio is approximately 16:9
      if (Math.abs(aspectRatio - targetAspectRatio) <= tolerance) {
        resolve({ isValid: true, width, height, aspectRatio });
      } else {
        const error = `Thumbnail - Image aspect ratio must be 16:9. Current ratio is ${width}:${height} (${aspectRatio.toFixed(
          2
        )}:1)`;
        reject(new Error(error));
      }
    };

    img.onerror = () => {
      clearTimeout(timeout);
      const errorMsg =
        "Thumbnail - Failed to load image. Please check if the image URL is valid.";
      reject(new Error(errorMsg));
    };

    img.src = imageUrl;
  });
};

export const formatDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const day = String(date.getDate());
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

/**
 * Convert the Incoming array to dropdown option array
 * @param {*} inputOption
 * @returns
 */
export const getDropDownOptionValues = (inputOption = [], isSme = false) => {
  let dropDownOptions = [];
  inputOption?.forEach((item) => {
    if (item?.active || item?.status || isSme) {
      dropDownOptions.push({
        id: item?.id,
        label: item?.name,
        name: item?.name,
        description: item?.description,
      });
    }
  });

  return dropDownOptions;
};

export const getDisclaimerDetailsFromId = (
  disclaimerId,
  lookupDisclaimerData
) => {
  let disclaimerContent = "";
  lookupDisclaimerData.forEach((item) => {
    if (item.id === disclaimerId) {
      disclaimerContent = item?.description;
    }
  });

  return disclaimerContent;
};

export const mergeUnique = (arr1, arr2) => {
  const uniqueArr = [...arr1];
  const uniqueSet = new Set(uniqueArr.map((el) => el.id));
  arr2.forEach((ele) => {
    if (uniqueSet.has(ele.id)) return;
    uniqueSet.add(ele.id);
    uniqueArr.push(ele);
  });
  return uniqueArr;
};

export const getPepsicoDropdownOptions = (optionData) => {
  const optionArray = [];
  optionData?.forEach((item) => {
    optionArray.push({ id: item?.id, displayText: item?.name });
  });

  return optionArray;
};

export const getDisplayTextForDropDown = (selectedItemId, lookupData) => {
  const selectedItem = lookupData?.filter((item) => item.id === selectedItemId);

  return selectedItem;
};

/**
 * Convert the Incoming array to dropdown option array
 * @param {*} inputOption
 * @returns
 */
export const getDropDownOptionValuesForCuratorFilters = (inputOption = []) => {
  let dropDownOptions = [];
  inputOption?.forEach((item) => {
    dropDownOptions.push({
      id: item?.id,
      label: item?.name,
      description: item?.description,
      active: item?.active,
    });
  });
  return dropDownOptions;
};

/**
 * Format date with dd-mmm-yyyy and return
 * @param {string|number|Date} [input] - date to format; defaults to now when omitted or invalid
 * @returns date string
 */
export function getFormattedDate(input) {
  const parsed = input ? new Date(input) : new Date();
  const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed;

  const day = String(date.getDate()).padStart(2, "0"); // Get day and pad with zero if needed
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()]; // Get month as abbreviated name
  const year = date.getFullYear(); // Get full year

  return `${day}-${month}-${year}`;
}

/**
 * Return the accordian content
 * @param {*} accordion
 * @returns
 */

export const accordionContent = (accordion) => {
  return [
    {
      label: "Content type",
      content: capitalizeFirstLetter(accordion?.contentType),
    },
    {
      label: "Sector",
      content: accordion?.sector,
    },
    {
      label: "Country",
      content: accordion?.country,
    },
    {
      label: "Uploaded by",
      content: accordion?.uploadedBy,
    },
    {
      label: "Language",
      content: accordion?.language,
    },
    {
      label: "Visibility",
      content: accordion?.visibility,
    },
    {
      label: "Version Number",
      content: accordion?.versionNumber,
    },
    {
      label: "Updated Date",
      content: formatDate(accordion?.uploadedDate),
    },
    {
      label: "Content Level",
      content: accordion?.contentLevel,
    },
    {
      label: "Legal Review",
      content: accordion?.legalReview,
    },
    {
      label: "Special Projects",
      content:
        accordion?.specialProjects?.length > 0
          ? accordion?.specialProjects?.join()
          : "",
    },
    {
      label: "Crop Type",
      content: accordion?.cropType,
    },
  ];
};

/**
 * Return the section content
 * @param {*} accordion
 * @returns
 */
export const generateSectionData = (accordion) => {
  const sectionData = [
    {
      label: "Subjects",
      content: accordion?.subjects?.length > 0 ? [accordion?.subjects] : [],
    },
    {
      label: "Keywords",
      content: accordion?.keywords?.length > 0 ? [accordion?.keywords] : [],
    },
  ];
  return sectionData;
};

/**
 * Convert string and make first letter capital
 * @param {*} string
 * @returns
 */
export const capitalizeFirstLetter = (string) => {
  return string?.charAt(0).toUpperCase() + string?.slice(1).toLowerCase();
};

export const prepareCurtatorTableData = (tableData = []) => {
  const formatedTableData = [];

  tableData.forEach((item) => {
    formatedTableData.push({
      ...item,
      sector: displayArrayValueAsCommaSeperated(item?.sector),
      country: displayArrayValueAsCommaSeperated(item?.country),
      language: displayArrayValueAsCommaSeperated(item?.language),
      subject: displayArrayValueAsCommaSeperated(item?.subject),
    });
  });

  return formatedTableData;
};

export const getTheCsvArrayValue = (csvArrayData) => {
  if (Array.isArray(csvArrayData) && csvArrayData?.length > 0) {
    return csvArrayData?.join(",");
  } else {
    return csvArrayData;
  }
};

export const escapeCsvValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  if (typeof value === "string") {
    if (value.includes('"')) {
      value = value.replace(/"/g, '""');
    }
    if (value.includes(",") || value.includes("\n") || value.includes('"')) {
      value = `"${value}"`;
    }
  }
  return value;
};

export const curatorCSVFileDownload = (csvData, filename) => {
  const csvRows = [];
  const headers = [
    "Content Title",
    "Content Type",
    "Status",
    "Sector",
    "Country",
    "Language",
    "Subject",
  ];
  csvRows.push(headers?.join(","));
  csvData?.forEach((row) => {
    const values = [
      escapeCsvValue(row?.title),
      escapeCsvValue(row?.type),
      escapeCsvValue(row?.status),
      escapeCsvValue(getTheCsvArrayValue(row?.sector)),
      escapeCsvValue(getTheCsvArrayValue(row?.country)),
      escapeCsvValue(getTheCsvArrayValue(row?.language)),
      escapeCsvValue(getTheCsvArrayValue(row?.subject)),
    ];
    csvRows.push(values?.join(","));
  });
  const csvContent = csvRows?.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export const getActiveItems = (items) => {
  return items
    .filter((item) => item.active) // Filter active items
    .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name
};

export const sortTableData = (data, params) => {
  return data.sort((a, b) => {
    const nameA = a[params]?.toLowerCase() || ""; // Handle case insensitivity and null values
    const nameB = b[params]?.toLowerCase() || "";
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });
};

export const urlTagging = async (fetch, params) => {
  const updatedItems = await Promise.all(
    fetch.map(async (item) => {
      const data = await fetchAndCacheImage(item?.[params]);
      return { ...item, thumbnailUrl: data }; // Create a new object with the `url` property
    })
  );
  return updatedItems;
};

export const displayArrayValueAsCommaSeperated = (contentValue) => {
  if (Array.isArray(contentValue)) {
    return contentValue?.join(", ");
  } else {
    return contentValue;
  }
};

// export const increaseViewCount = (
//   globalState,
//   docId,
//   content,
//   views,
//   likes
// ) => {
//   const contentKeyMap = {
//     Documents: "documents",
//     Links: "links",
//     Videos: "videos",
//   };

//   const contentKey = contentKeyMap[content];

//   if (!contentKey) {
//     console.log("Invalid content type");
//     return globalState;
//   }

//   const subPageIndex = globalState.internalPageSections.findIndex((subPage) =>
//     subPage[contentKey].some((item) => item.id === docId)
//   );

//   if (subPageIndex === -1) {
//     console.log(`${content} not found in any subPage`);
//     return globalState; // Exit if the item is not found
//   }

//   // Update the likes and views fields for the matched item
//   const updatedItems = globalState.internalPageSections[subPageIndex][
//     contentKey
//   ].map((item) =>
//     item.id === docId ? { ...item, likes: likes, views: views } : item
//   );

//   // Update globalState while keeping the hierarchy
//   return {
//     ...globalState,
//     internalPageSections: globalState.internalPageSections.map(
//       (subPage, index) =>
//         index === subPageIndex
//           ? { ...subPage, [contentKey]: updatedItems } // Only update the matched subPage
//           : subPage
//     ),
//   };
// };

export const toggleFavourite = (globalState, contentId, content, favorite) => {
  console.log(
    favorite,
    contentId,
    content,
    globalState,
    "-------modifiedFavourite------"
  );
  const contentKeyMap = {
    Documents: "documents",
    Links: "links",
    Videos: "videos",
  };

  const contentKey = contentKeyMap[content];

  const subPageIndex = globalState?.internalPageSections?.findIndex((subPage) =>
    subPage[contentKey].some((item) => item.id === contentId)
  );

  if (subPageIndex === -1) {
    return globalState; // Exit if the item is not found
  }

  const updatedItems = globalState?.internalPageSections?.[subPageIndex][
    contentKey
  ]?.map((item) => {
    if (item.id === contentId) {
      return { ...item, favorite: favorite };
    } else {
      return item;
    }
  });

  return {
    ...globalState,
    internalPageSections: globalState?.internalPageSections?.map(
      (subPage, index) =>
        index === subPageIndex
          ? { ...subPage, [contentKey]: updatedItems } // Only update the matched subPage
          : subPage
    ),
  };
};

export const libraryToggle = (globalState, contentId, content, favorite) => {
  const contentKeyMap = {
    Documents: "documents",
    Links: "links",
    Videos: "videos",
  };

  const contentKey = contentKeyMap[content];

  // Find the index of the subject that contains the contentId
  const subPageIndex = globalState?.subjectItems?.findIndex((subPage) =>
    subPage[contentKey]?.some((item) => item.id === contentId)
  );

  if (subPageIndex === -1) {
    console.log("Content not found in subjectItems");
    return globalState; // Return unchanged state if no match found
  }

  // Update only the matching content items
  const updatedItems = globalState?.subjectItems?.[subPageIndex][
    contentKey
  ]?.map((item) =>
    item.id === contentId ? { ...item, favorite: favorite } : item
  );

  // Return the updated global state with nested structure fixed
  return {
    ...globalState,

    subjectItems: globalState?.subjectItems?.map((subPage, index) =>
      index === subPageIndex
        ? { ...subPage, [contentKey]: updatedItems } // Update only the specific subject
        : subPage
    ),
  };
};

export const truncateArray = (array, max = 3) => {
  if (!array || array?.length === 0) return "-";
  return array?.length > max
    ? `${array?.slice(0, max).join(", ")} +${array?.length - max} more`
    : array?.join(", ");
};
export function truncateText(text, maxLength) {
  return text?.length > maxLength
    ? text?.substring(0, maxLength) + "..."
    : text;
}
export const uploadFileAndGiveFileId = async (files) => {
  const fileId = await Promise.all(
    files.map(async (item) => {
      const formData = new FormData();
      formData.append("file", item);
      const response1 = await axios.post(
        `${FILE_SERVICE_BASE_URL}/fritolay/file-manager/file/upload?user=ADMIN`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response1.status === 200) {
        return response1.data.fileId;
      } else {
        showErrorToast("Failed To Upload ");
      }
    })
  );
  return fileId;
};

export const uploadThumbnailAndGiveFileId = async (
  files,
  validation = true,
  onUploadProgress
) => {
  // Normalize to array if single file is passed
  const filesArray = Array.isArray(files) ? files : [files];

  // Validate aspect ratio for all files before uploading
  if (validation) {
    try {
      await Promise.all(
        filesArray.map((file) => validateThumbnailAspectRatio(file))
      );
    } catch (error) {
      throw error; // Re-throw validation error
    }
  }

  const fileId = await Promise.all(
    filesArray.map(async (item) => {
      const formData = new FormData();
      formData.append("file", item);
      const response1 = await axios.post(
        `${FILE_SERVICE_BASE_URL}/fritolay/file-manager/thumbnail/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (onUploadProgress && progressEvent.total) {
              onUploadProgress(
                Math.round((progressEvent.loaded / progressEvent.total) * 100)
              );
            }
          },
        }
      );
      if (response1.status === 200) {
        return response1.data.fileId;
      } else {
        showErrorToast("Thumbnail - Failed To Upload ");
      }
    })
  );
  return fileId;
};

export const removeItemByContentId = (data, contentId) => {
  return {
    ...data,
    internalPageSections: data?.internalPageSections?.map((page) => ({
      ...page,
      documents: page?.documents?.filter((doc) => doc.id !== contentId),
      links: page?.links?.filter((link) => link.id !== contentId),
      videos: page?.videos?.filter((video) => video.id !== contentId),
    })),
  };
};

export const addItemByContentType = (data, newItem) => {
  // Ensure data has the expected structure
  const initializedData = {
    internalPageSections: data?.internalPageSections?.length
      ? data.internalPageSections
      : [{ documents: [], links: [], videos: [] }],
  };

  return {
    ...initializedData,
    internalPageSections: initializedData.internalPageSections.map((page) => ({
      ...page,
      documents:
        newItem.contentType === "DOCUMENT"
          ? [...(page.documents || []), newItem]
          : page.documents,
      links:
        newItem.contentType === "LINK"
          ? [...(page.links || []), newItem]
          : page.links,
      videos:
        newItem.contentType === "VIDEO"
          ? [...(page.videos || []), newItem]
          : page.videos,
    })),
  };
};

export function saveOctetStreamToFile(
  octetStream,
  fileName,
  mimeType = "application/octet-stream"
) {
  // Create a Blob from the Octet Stream
  const blob = new Blob([octetStream], { type: mimeType });

  // Convert Blob to a File
  const file = new File([blob], fileName, { type: mimeType });

  // Create a download link for the file
  const link = document.createElement("a");
  link.href = URL.createObjectURL(file);
  link.download = fileName;

  // Trigger the download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const openItem = async (fileId, contentUrl, contentType) => {
  if (contentType === "LINK") {
    if (contentUrl === "") {
      return Promise.reject("Unable to open the link");
    }
    window.open(contentUrl, "_blank", "flag");
    return Promise.resolve("Link opened");
  } else {
    return downloadFileToLocal(fileId, true)
      .then((response) => {
        if (response?.status === 200) {
          return Promise.resolve("File downloaded successfully");
        } else {
          return Promise.reject("Something went wrong while downloading file");
        }
      })
      .catch((error) => {
        console.log("error in file download", error);
        return Promise.reject("Something went wrong while downloading file");
      });
  }
};

export const getCountryList = (sectorIds, secotrByCountrylist = []) => {
  const selectedCountryList = [];
  let countryList = [];
  sectorIds?.forEach((item) => {
    secotrByCountrylist.forEach((countryData) => {
      if (countryData?.id === item) {
        selectedCountryList.push(countryData);
      }
    });
  });

  selectedCountryList?.forEach((item) => {
    countryList.push(...(item?.countries ?? []));
  });

  return countryList;
};

export const handleShare = (location) => {
  const urlToShare = `${window.location.origin}${location?.pathname}`; // You can replace this with the specific URL you want to copy
  navigator.clipboard
    ?.writeText(urlToShare)
    ?.then(() => {
      showSuccessToast("Link copied to clipboard!");
    })
    .catch((error) => {
      console.error("Failed to copy text: ", error);
      alert("Failed to copy the link. Please try again.");
    });
};

export const handleSectorSelection = (
  sector,
  localSector,
  setLocalSector,
  dispatch,
  setCountries,
  countryFilter,
  sectorAndCountry,
  action
) => {
  if (!sector) {
    // If sector is deselected, reset state
    setLocalSector(null);
    dispatch(action("")); // Reset sector filter
    setCountries(countryFilter); // Reset country list
    return;
  }

  if (localSector && sector.id === localSector.id) {
    // If the same sector is selected again, reset selection
    setLocalSector(null);
    setCountries(countryFilter);
    dispatch(action(""));
  } else {
    // Otherwise, update sector
    setLocalSector(sector);
    dispatch(action(sector));

    // Update country list based on sector selection
    const country = sectorAndCountry[sector.label] || [];
    setCountries(country);
  }
};

export const handleResizeStart = (e, setDrawerHeight) => {
  e.preventDefault();

  const handleResize = (moveEvent) => {
    const currentY = moveEvent.clientY || moveEvent.touches[0].clientY;
    const newHeight = `calc(100% - ${currentY}px)`;
    setDrawerHeight(newHeight);
  };

  const stopResize = () => {
    window.removeEventListener("mousemove", handleResize);
    window.removeEventListener("mouseup", stopResize);
    window.removeEventListener("touchmove", handleResize);
    window.removeEventListener("touchend", stopResize);
  };

  window.addEventListener("mousemove", handleResize);
  window.addEventListener("mouseup", stopResize);
  window.addEventListener("touchmove", handleResize);
  window.addEventListener("touchend", stopResize);
};

export const getImagePreview = (newImage) => {
  if (!newImage || !newImage.type) {
    return Object.assign(noPreviewImage, {
      preview: noPreviewImage,
    });
  }
  if (newImage.type.startsWith("image/")) {
    return Object.assign(newImage, {
      preview: URL.createObjectURL(newImage),
    });
  }
  return Object.assign(noPreviewImage, {
    preview: noPreviewImage,
  });
};

export const handleDragEnd = async (
  event,
  localState,
  arrayMove,
  updateState,
  globalState,
  setLoading,
  dispatch,
  string
) => {
  const { active, over } = event;
  if (active.id !== over?.id) {
    const oldIndex = localState.findIndex((item) => item.id === active.id);
    const newIndex = localState.findIndex((item) => item.id === over?.id);
    const newArray = arrayMove(localState, oldIndex, newIndex)?.map(
      (item, idx) => ({
        ...item,
        contentId: item.contentId,
        slotNumber: idx + 1, // update slot number
      })
    );
    const payload = newArray?.map((item) => {
      return { contentId: item.contentId, slotNumber: item.slotNumber };
    });

    updateState(newArray);
    dispatch(globalState(newArray)); // if needed in global state
    setLoading(true);
    try {
      await axios.put(
        `${BASE_URL}${string}`,
        payload,

        {
          headers: {
            Authorization: `Bearer ${token}`,

            "Content-Type": "application/json",
          },
        }
      );
    } catch {
    } finally {
      setLoading(false);
    }
  }
};

export const toggleSpotlightFavourtie = (
  items,
  contentId,
  newFavorite // optional override, else toggles
) => {
  return items.map((item) =>
    item.contentId === contentId
      ? {
          ...item,
          favorite: newFavorite !== undefined ? newFavorite : !item.favorite,
        }
      : item
  );
};

// Extract INTERNAL routes with slug from navigation JSON
export function extractInternalRoutes(navigation) {
  const result = [];

  function traverse(nodes) {
    nodes.forEach((node) => {
      if (node.type === "INTERNAL") {
        if (node.url) {
          const slug = node.url.startsWith("/")
            ? node.url.substring(1)
            : node.url;
          result.push({
            ...node,
            slug: slug,
          });
        }
      }

      // If node has children, check them too
      if (Array.isArray(node.children) && node.children.length > 0) {
        traverse(node.children);
      }
    });
  }

  traverse(navigation);
  return result;
}

export function extractInternalGroups(navigation) {
  return navigation
    .filter((item) => item.type === "INTERNAL_GROUP")
    .map((group) => ({
      navigationId: group.navigationId,
      label: group.label,
      type: group.type,
    }));
}

// Example usage:

// Example usage:
// const navigationData = [/* your big JSON here */];

// const internalRoutes = extractInternalRoutes(navigationData);
// console.log(internalRoutes);

/**
 * Common language lookup utility
 * Fetches language data and provides helper functions
 */
let languageCache = null;
let languageCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const getLanguageData = async () => {
  const now = Date.now();

  // Return cached data if it's still valid
  if (
    languageCache &&
    languageCacheTimestamp &&
    now - languageCacheTimestamp < CACHE_DURATION
  ) {
    return languageCache;
  }

  try {
    const response = await getLanguageLookUpData();
    if (response && response.length > 0) {
      const activeItems = response.filter((item) => item.isActive !== false);
      languageCache = activeItems;
      languageCacheTimestamp = now;
      return activeItems;
    }
    return [];
  } catch (error) {
    console.error("Error fetching language data:", error);
    return [];
  }
};

/**
 * Get language name by language ID
 * @param {number} languageId - The language ID to look up
 * @returns {string} - The language name or "Unknown" if not found
 */
export const getLanguageNameById = async (languageId) => {
  const languages = await getLanguageData();
  const language = languages.find((lang) => lang.id === languageId);
  return language ? language.name : "Unknown";
};

/**
 * Get language name by language ID (synchronous version using cache)
 * @param {number} languageId - The language ID to look up
 * @returns {string} - The language name or "Unknown" if not found
 */
export const getLanguageNameByIdSync = (languageId) => {
  if (!languageCache) {
    return "Unknown";
  }
  const language = languageCache.find((lang) => lang.id === languageId);
  return language ? language.name : "Unknown";
};

/**
 * Component to conditionally show tooltip only when text is truncated
 * @param {string} value - The text value to display
 * @param {string} fullTitle - The full title to show in tooltip
 * @returns {JSX.Element}
 */
export const TitleWithTooltip = ({ value, fullTitle }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    // Check if text is truncated after render
    const checkTruncation = () => {
      if (textRef.current) {
        const isTextTruncated =
          textRef.current.scrollHeight > textRef.current.clientHeight;
        setShowTooltip(isTextTruncated);
      }
    };

    // Check immediately
    checkTruncation();

    // Also check after a short delay to ensure the element is fully rendered
    const timeoutId = setTimeout(checkTruncation, 100);

    return () => clearTimeout(timeoutId);
  }, [fullTitle]);

  const content = (
    <Typography
      ref={textRef}
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        cursor: "pointer",
      }}
    >
      {value}
    </Typography>
  );

  if (showTooltip) {
    return (
      <Tooltip title={fullTitle} arrow enterDelay={2000}>
        {content}
      </Tooltip>
    );
  }

  return content;
};

/**
 * Validates VTT file before upload (size and extension)
 * @param {File} file - The file to validate
 * @param {number} maxSizeInMB - Maximum file size in MB (default: 10)
 * @returns {Object} - { isValid: boolean, error?: string }
 */
export const validateVttFileBeforeUpload = (file, maxSizeInMB = 10) => {
  const MAX_FILE_SIZE = maxSizeInMB * 1024 * 1024;

  if (!file) {
    return { isValid: false, error: "No file provided" };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size exceeds the ${maxSizeInMB} MB limit. Please upload a smaller file.`,
    };
  }

  // Check if file is a valid VTT format (only VTT allowed)
  const fileExtension = "." + file.name.split(".").pop().toLowerCase();
  if (fileExtension !== ".vtt") {
    return {
      isValid: false,
      error: "Please upload a valid VTT file (.vtt format only)",
    };
  }

  return { isValid: true };
};

/**
 * Uploads and validates a VTT file
 * @param {File} file - The file to upload
 * @param {number} languageId - The language ID
 * @param {Array} languageOptions - Array of language options
 * @param {string} fileType - Type of file ('chapter' or 'subtitle')
 * @returns {Promise<Object>} - { success: boolean, fileId?: string, fileData?: Object, error?: string }
 */
export const uploadAndValidateVttFile = async (
  file,
  languageId,
  languageOptions = [],
  fileType = "file"
) => {
  try {
    // Step 1: Upload file to get fileId
    const fileIds = await uploadFileAndGiveFileId([file]);
    const uploadedFileId = fileIds[0];

    // Step 2: Validate VTT file
    const validationResult = await validateVttFile(uploadedFileId);

    if (!validationResult.isValid) {
      // Delete the uploaded file if validation fails
      try {
        await deleteCuratorFile(uploadedFileId);
      } catch (deleteError) {
        console.error("Error deleting invalid file:", deleteError);
      }

      return {
        success: false,
        error:
          validationResult.error ||
          "Invalid VTT file. Please check the file format and try again.",
      };
    }

    // Step 3: Create file data object
    const languageName =
      languageOptions.find((lang) => lang.id === languageId)?.name ||
      (fileType === "chapter" ? "English" : "Unknown");

    const fileData = {
      id: Date.now(),
      file: file,
      fileId: uploadedFileId,
      languageId: languageId,
      languageName: languageName,
      fileName: file.name,
      fileSize: file.size,
      uploadDate: new Date().toISOString(),
    };

    return {
      success: true,
      fileId: uploadedFileId,
      fileData: fileData,
    };
  } catch (error) {
    console.error(`Error uploading ${fileType} file:`, error);
    return {
      success: false,
      error: `Failed to upload ${fileType} file`,
    };
  }
};

/**
 * Handles deletion of a VTT file
 * @param {string} fileId - Internal file ID
 * @param {Array} files - Array of file objects
 * @param {Function} setFiles - State setter for files
 * @param {Function} setFileDetails - Optional state setter for parent file details
 * @param {Function} setApiData - Optional state setter for API data
 * @param {string} fileInputId - ID of the file input element to reset
 * @returns {Promise<Object>} - { success: boolean, error?: string }
 */
export const handleVttFileDelete = async (
  fileId,
  files,
  setFiles,
  setFileDetails = null,
  setApiData = null,
  fileInputId = null
) => {
  // Find the file to get its fileId for the API data
  const fileToDelete = files.find((file) => file.id === fileId);

  if (!fileToDelete) {
    return { success: false, error: "File not found" };
  }

  try {
    // Delete file from server if it has a fileId (was uploaded)
    if (fileToDelete.fileId) {
      await deleteCuratorFile(fileToDelete.fileId);
    }

    // Remove from local state
    setFiles((prev) => prev.filter((file) => file.id !== fileId));

    // Update parent component's file details
    if (typeof setFileDetails === "function") {
      setFileDetails((prev) => prev.filter((file) => file.id !== fileId));
    }

    // Update API data
    if (typeof setApiData === "function") {
      setApiData((prev) =>
        prev.filter((item) => item.fileId !== fileToDelete.fileId)
      );
    }

    // Reset the file input to allow uploading the same file again
    if (fileInputId) {
      const fileInput = document.getElementById(fileInputId);
      if (fileInput) {
        fileInput.value = "";
      }
    }

    return { success: true };
  } catch (error) {
    console.error("Error deleting file:", error);
    return {
      success: false,
      error: "Failed to delete file. Please try again.",
    };
  }
};

/**
 * Handles viewing/opening a VTT document
 * @param {string} fileId - The file ID to view
 * @param {Function} setViewingFileId - State setter for viewing file ID
 * @param {string} fileInternalId - Internal file ID for state management
 * @returns {Promise<Object>} - { success: boolean, error?: string }
 */
export const handleViewVttDocument = async (
  fileId,
  setViewingFileId,
  fileInternalId
) => {
  if (!fileId) {
    return { success: false, error: "File ID not found" };
  }

  setViewingFileId(fileInternalId);
  try {
    const response = await downloadFileToLocal(fileId, true);
    if (response?.status === 200) {
      return { success: true };
    } else {
      return { success: false, error: "Failed to open file" };
    }
  } catch (error) {
    console.error("Error opening file:", error);
    return { success: false, error: "Failed to open file" };
  } finally {
    setViewingFileId(null);
  }
};

/**
 * Prepares language options for dropdown with upload status
 * @param {Array} languageOptions - Array of language options
 * @param {Array} files - Array of uploaded files
 * @returns {Array} - Array of language options with upload status
 */
export const prepareLanguageOptionsForDropdown = (languageOptions, files) => {
  return languageOptions.map((item) => {
    const hasExistingFile = files.some((file) => file.languageId === item.id);
    return {
      id: item.id,
      displayText: hasExistingFile
        ? `${item.name} (Already uploaded)`
        : item.name,
      disabled: hasExistingFile,
    };
  });
};
